import React, { useEffect, useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { AutomatedCreditControlResult } from '../../graphql/entities';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import {
  Direction,
  FilterOperation,
  IAutomatedCreditControlResult,
  IAutomatedCreditControlResultsQueryResult,
  IAutomatedCreditControlResultsQueryVariables,
  IAutomatedCreditControlStage,
  IAutomatedCreditControlStagesQueryResult,
  IAutomatedCreditControlStagesQueryVariables,
  IFilterInput,
  NivoGraphPattern,
} from '../../graphql/schema/p4types';
import { SalesTransactionExpansion } from '../SalesTransactionExpansion';
import Formatters from '../../components/grid-addons/formatters';
import { Paths } from '../../@types/keyOfNested';
import { useQuery } from '@apollo/client';
import AutomatedCreditControlStageWorkspaceQuery from '../../graphql/queries/salesLedger/AutomatedCreditControlStageWorkspaceQuery.graphql';
import {
  PatternDotsDef,
  patternDotsDef,
  PatternLinesDef,
  patternLinesDef,
  PatternSquaresDef,
  patternSquaresDef,
  SvgFillMatcher,
} from '@nivo/core';
import {
  defaultBackgroundColour,
  defaultLabelTextColour,
  IWorkspaceOverviewBarProps,
} from '../../components/Workspace/WorkspaceOverviewBar';
import NumericFormatter from '../../components/grid-addons/formatters/NumericFormatter';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { ExpansionUUIDProvider } from '../../components/Context/ExpansionUUIDProvider';

const DEFAULT_COLUMNS: Array<Paths<IAutomatedCreditControlResult>> = [
  'customer.reference',
  'automatedCreditControlStage.actionType',
  'commentary',
  'totalOutstandingValue',
  'salesTransactions',
];

const jobFilterFromProps = (props: { match?: { params?: { jobid?: string } } }): Array<IFilterInput> => {
  if (props?.match?.params?.jobid && props.match.params.jobid !== ':jobid') {
    return [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'jobId',
          value: props.match.params.jobid,
        },
      },
    ];
  }
  return [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'preview',
        value: 'false',
      },
    },
  ];
};

type INivoGraphPatternOptions = {
  dots?: Omit<PatternDotsDef, 'id' | 'type'>;
  lines?: Omit<PatternLinesDef, 'id' | 'type'>;
  squares?: Omit<PatternSquaresDef, 'id' | 'type'>;
};

export const getPatternForStage = (stage: IAutomatedCreditControlStage, options?: INivoGraphPatternOptions) => {
  const name = stage.name;
  switch (stage.pattern) {
    case NivoGraphPattern.Dots:
      return patternDotsDef(name!, {
        background: 'inherit',
        color: stage.patternColour ?? undefined,
        stagger: true,
        ...(options?.dots || {}),
      });
    case NivoGraphPattern.Lines:
      return patternLinesDef(name!, {
        background: 'inherit',
        color: stage.patternColour ?? undefined,
        rotation: -45,
        lineWidth: 6,
        spacing: 10,
        ...(options?.lines || {}),
      });
    case NivoGraphPattern.Squares:
      return patternSquaresDef(name!, {
        background: 'inherit',
        color: stage.patternColour ?? undefined,
        ...(options?.squares || {}),
      });
  }
};

export const useAutomatedCreditControlGraphStyle = (
  stages: Array<IAutomatedCreditControlStage>,
  options?: INivoGraphPatternOptions
) => {
  return useMemo<Omit<IWorkspaceOverviewBarProps, 'listData'>>(() => {
    const fill: Array<{ id: string; match: Record<string, unknown> | SvgFillMatcher<any> | '*' }> = [];
    const backgroundColourMap: { [key: string]: `#${string}` } = {};
    const textColourMap: { [key: string]: `#${string}` } = {};
    const defs = stages
      ?.map((stage) => {
        const name = stage.name!;
        fill.push({
          match: {
            id: name,
          },
          id: name,
        });
        backgroundColourMap[name] = stage.backgroundColour as `#${string}`;
        textColourMap[name] = stage.textColour as `#${string}`;
        if (stage.pattern && stage.pattern !== NivoGraphPattern.None) {
          return getPatternForStage(stage, options);
        }
        return null;
      })
      ?.filter((def) => def !== null);

    return {
      defs,
      fill,
      backgroundColour: (groupColumnKey, index) =>
        backgroundColourMap[groupColumnKey] || defaultBackgroundColour(groupColumnKey, index),
      labelTextColour: (groupColumnKey, index) =>
        textColourMap[groupColumnKey] || defaultLabelTextColour(groupColumnKey, index),
    };
  }, [options, stages]);
};

const useAutomatedCreditControlOverviewBarProps = (listData: IAutomatedCreditControlResultsQueryResult['listData']) => {
  //Set up a filter so we only fetch the stages being displayed.
  const groupNameFilters = listData?.aggregateRows
    ?.flatMap((aggregateRow) => aggregateRow?.groupColumns?.[0]?.value)
    ?.map((groupName) => {
      return {
        filterOperation: FilterOperation.In,
        filterParameters: {
          field: 'name',
          value: groupName,
        },
      };
    });
  const { data, fetchMore, loading } = useQuery<
    IAutomatedCreditControlStagesQueryResult,
    IAutomatedCreditControlStagesQueryVariables
  >(AutomatedCreditControlStageWorkspaceQuery, {
    skip: !groupNameFilters,
    variables: {
      filters: groupNameFilters,
    },
    fetchPolicy: 'cache-and-network',
  });

  //This effect will run fetchMore calls until we have all stages fetched.
  useEffect(() => {
    if (!loading && data?.listData?.pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.listData?.pageInfo.endCursor,
        },
      });
    }
  }, [data?.listData?.pageInfo.endCursor, data?.listData?.pageInfo.hasNextPage, fetchMore, loading]);

  const stages = useMemo(() => {
    return data?.listData?.edges?.map((value: any) => value?.node) as Array<IAutomatedCreditControlStage>;
  }, [data?.listData?.edges]);

  return useAutomatedCreditControlGraphStyle(stages);
};

const availableColumnsFilter = (column: IEditableDataGridColumnDef) => {
  return !column?.fieldName?.includes('salesTransactions.');
};

const AutomatedCreditControlResultWorkspace = (props: any) => {
  const columns = useMemo<Array<Paths<IAutomatedCreditControlResult>>>(() => {
    if (props?.match?.params?.jobid) {
      return ['preview', ...(DEFAULT_COLUMNS as any)] as Array<Paths<IAutomatedCreditControlResult>>;
    }
    return DEFAULT_COLUMNS;
  }, [props?.match?.params?.jobid]);

  const workspaceProps = useWorkspace<
    IAutomatedCreditControlResult,
    IAutomatedCreditControlResultsQueryResult,
    IAutomatedCreditControlResultsQueryVariables
  >(props, AutomatedCreditControlResult, {
    columns: columns,
    initialSortColumn: 'commentary',
    filters: jobFilterFromProps(props),
    columnOverrides: useMemo(
      () => ({
        'automatedCreditControlStage.name': { name: 'ACC Stage - Name' },
        'automatedCreditControlStage.actionType': { name: 'ACC Stage - Action' },
        'automatedCreditControlStage.daysFromDueDate': { name: 'ACC Stage - Days from Due Date' },
        'automatedCreditControlStage.automatedCreditControlWorkflow.name': { name: 'ACC Workflow - Name' },
        totalOutstandingValue: {
          name: 'Outstanding Transaction Value',
          formatter: NumericFormatter,
          options: { format: { decimals: 2 }, suppressTrailingZeros: false, suppressZeroes: false },
        },
        'automatedCreditControlStage.automatedCreditControlWorkflow.overdueBalanceThreshold': {
          name: 'ACC Workflow - Threshold',
          formatter: NumericFormatter as any,
          options: { format: { decimals: 2 }, suppressTrailingZeros: false, suppressZeroes: false },
        },
        salesTransactions: {
          name: 'Sales Transactions',
          formatter: Formatters.ExpansionFormatter as any,
          sortable: false,
          options: {
            format: { decimals: 2 },
            suppressTrailingZeros: false,
            suppressZeroes: false,
            formatter: Formatters.TextFieldFormatter as any,
            displayKey: 'baseOutstandingAmount',
          },
        },
      }),
      []
    ),
    additionalVariables: {
      aggregateCriteria: {
        //Secondary grouping on daysFromDueDate should have no notable effect, but it allows the sort column to function.
        groupColumns: ['automatedCreditControlStage.name', 'automatedCreditControlStage.daysFromDueDate'],
        sumColumns: ['totalOutstandingValue'],
        sortColumns: ['automatedCreditControlStage.daysFromDueDate'],
        sortDirection: Direction.Asc,
      },
    },
  });

  const workspaceOverviewBarProps = useAutomatedCreditControlOverviewBarProps(workspaceProps.listData);

  return (
    <ExpansionUUIDProvider>
      <Workspace
        entityType={AutomatedCreditControlResult.type}
        title={AutomatedCreditControlResult.title}
        formUrl={AutomatedCreditControlResult.formUrl}
        defaultQuery={AutomatedCreditControlResult.workspace}
        predefinedFilters={jobFilterFromProps(props)}
        availableColumnsFilter={availableColumnsFilter}
        availableFiltersFilter={availableColumnsFilter}
        expandableRows={true}
        rowRenderer={SalesTransactionExpansion}
        {...workspaceProps}
        {...props}
        entity={AutomatedCreditControlResult}
        workspaceOverviewBarProps={workspaceOverviewBarProps}
      />
    </ExpansionUUIDProvider>
  );
};

export default AutomatedCreditControlResultWorkspace;
